import {
  hasSpaceBefore,
  parsedToWordTokens,
  wordTokensToScript,
  type WordToken,
} from "../../../../helpers/scriptHelpers";
import { useMemo, useState } from "react";
import { Space, Typography } from "antd";
import { NarrationText } from "../NarrationText";
import { PronunciationToken } from "./PronunciationToken";
import { useTranslate } from "#src/hooks";
import { useAntTheme } from "@/hooks/useAntTheme";
import { parseScript } from "pages/media/helpers/scriptAnchors";

export const Pronunciation = ({
  script,
  onChange,
  isRTL = false,
  language,
  sectionId,
  voiceId,
}: {
  script: string;
  onChange: (script: string) => void;
  isRTL?: boolean;
  language?: string;
  sectionId?: string;
  voiceId?: string;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const originalParsed = useMemo(() => parseScript(script), [script]);
  const tokens = useMemo(
    () => parsedToWordTokens(originalParsed, language),
    [originalParsed, language]
  );

  const [selectedToken, setSelectedToken] = useState<WordToken | null>(null);

  const handleWordClick = (token: WordToken) => {
    setSelectedToken(token);
  };

  const handleSave = (newTokens: WordToken[]) => {
    const result = wordTokensToScript(newTokens, originalParsed);
    onChange(result);
  };

  const handleAddPronunciation = (
    pronunciation: string | null,
    token: WordToken
  ) => {
    const newTokens = tokens.map((t) =>
      t.index === token.index
        ? { ...t, pronunciation: pronunciation ?? undefined }
        : t
    );
    handleSave(newTokens);
    setSelectedToken(null);
  };

  return (
    <Space direction="vertical" size="middle">
      <Typography.Text style={{ color: theme.colorPrimary }}>
        {t(
          "components.templates.templatePreview.voiceoverNarration.pronunciation.hint"
        )}
      </Typography.Text>
      <NarrationText $isRTL={isRTL}>
        {tokens.map((token) => {
          const nextToken = tokens[token.index + 1];
          const addSpaceAfterToken =
            !!nextToken && hasSpaceBefore(nextToken, token.index + 1);
          if (token.isPunctuation) {
            return (
              <span key={token.index}>
                {token.word}
                {addSpaceAfterToken ? " " : null}
              </span>
            );
          }
          return (
            <PronunciationToken
              key={token.index}
              token={token}
              sectionId={sectionId}
              voiceId={voiceId}
              isSelected={
                selectedToken?.index === token.index || !!token.pronunciation
              }
              onTokenClick={handleWordClick}
              addSpaceAfterToken={addSpaceAfterToken}
              onCancel={() => setSelectedToken(null)}
              onConfirm={(pronunciation) => {
                handleAddPronunciation(pronunciation, token);
              }}
            />
          );
        })}
      </NarrationText>
    </Space>
  );
};
